/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Mobile Navigation Component - Top navigation with hamburger menu
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import logo from '../data/banner/logo-xoa-background.png';

interface MobileNavProps {
  showDesignButton?: boolean;
}

const MobileNav: React.FC<MobileNavProps> = ({ showDesignButton = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleNavigate = (path: string) => {
    navigate(path);
    closeMenu();
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => handleNavigate('/')}
            className="flex items-center gap-2 min-h-[50px]"
          >
            <img src={logo} alt="Veritas Logo" className="h-14 md:h-20 w-auto object-contain" />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => handleNavigate('/phone-case')}
              className="text-slate-700 font-medium text-sm hover:text-slate-900 transition-colors min-h-[44px] px-3"
            >
              Ốp Lưng
            </button>
            <button
              onClick={() => handleNavigate('/cup')}
              className="text-slate-700 font-medium text-sm hover:text-slate-900 transition-colors min-h-[44px] px-3"
            >
              In Logo
            </button>
            <button
              onClick={() => handleNavigate('/history')}
              className="text-slate-700 font-medium text-sm hover:text-slate-900 transition-colors min-h-[44px] px-3 border-l pl-6 border-slate-100"
            >
              Lịch sử
            </button>
            {showDesignButton && (
              <button
                onClick={() => handleNavigate('/phone-case')}
                className="bg-slate-700 text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors min-h-[44px]"
              >
                Thiết Kế Ngay
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
            />

            {/* Slide-in Menu */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-50 md:hidden"
            >
              <div className="flex flex-col h-full">
                {/* Menu Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <span className="font-semibold text-lg text-slate-800">Menu</span>
                  <button
                    onClick={closeMenu}
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Menu Items */}
                <div className="flex-1 overflow-y-auto p-4">
                  <nav className="flex flex-col gap-2">
                    <button
                      onClick={() => handleNavigate('/')}
                      className="text-left px-4 py-3 text-slate-700 font-medium rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                    >
                      Trang Chủ
                    </button>
                    <button
                      onClick={() => handleNavigate('/phone-case')}
                      className="text-left px-4 py-3 text-slate-700 font-medium rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                    >
                      Thiết Kế Ốp Lưng
                    </button>
                    <button
                      onClick={() => handleNavigate('/cup')}
                      className="text-left px-4 py-3 text-slate-700 font-medium rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                    >
                      In Logo Doanh Nghiệp
                    </button>
                    <button
                      onClick={() => handleNavigate('/history')}
                      className="text-left px-4 py-3 text-slate-700 font-medium rounded-lg hover:bg-indigo-50 text-indigo-700 transition-colors min-h-[44px]"
                    >
                      Lịch Sử Thiết Kế
                    </button>
                  </nav>
                </div>

                {/* Menu Footer */}
                {showDesignButton && (
                  <div className="p-4 border-t border-gray-200">
                    <button
                      onClick={() => handleNavigate('/phone-case')}
                      className="w-full bg-slate-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors min-h-[44px]"
                    >
                      Bắt Đầu Thiết Kế
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNav;

